import { useCallback } from 'react';
import type { AppData, Dimension, Relationship, ImprovementArea, BusinessValueMetrics } from '../types';

// Type for the scale values
type Scale = 'Peak' | 'Emerging' | 'Weak';

// Type for dimension name to stars mapping
type DimensionStarsMap = Record<string, number>;

// Helper function to convert star rating to scale
const getScaleFromStars = (stars: number): Scale => {
  if (stars >= 4) return 'Peak';
  if (stars >= 2) return 'Emerging';
  return 'Weak';
};

// Utility function to validate relationships
const validateRelationships = (relationships: Relationship[], dimensionName: string): boolean => {
  const dimensionRelationships = relationships.filter(rel => rel.dimension === dimensionName);
  const totalWeight = dimensionRelationships.reduce((sum, rel) => sum + rel.weight, 0);
  return Math.abs(totalWeight - 1) < 0.001; // Allow for small floating point differences
};

// Function to calculate weighted maturity score
const calculateWeightedMaturityScore = (
  relationships: Relationship[],
  areas: Record<string, ImprovementArea[]>,
  dimensionName: string,
): number => {
  let totalWeight = 0;
  let weightedSum = 0;

  relationships
    .filter(rel => rel.dimension === dimensionName)
    .forEach(rel => {
      // Find the area in any quadrant
      for (const quadrantAreas of Object.values(areas)) {
        const area = quadrantAreas.find(a => a.name === rel.area);
        if (area && area.maturityScore !== null) {
          // Only include areas with non-null maturity scores in the calculation
          const maturityScore = area.maturityScore || 1;
          weightedSum += rel.weight * maturityScore;
          totalWeight += rel.weight;
          break;
        }
      }
    });

  return totalWeight > 0 ? weightedSum / totalWeight : 1;
};

// Function to calculate star rating from percentage
const calculateStarRating = (percentage: number): number => {
  if (percentage >= 90) return 5;
  if (percentage >= 70) return 4;
  if (percentage >= 50) return 3;
  if (percentage >= 30) return 2;
  return 1;
};

// Return type for the hook
interface UseStarRatingCalculationReturn {
  recalculateStarRatings: (data: AppData) => AppData;
  recalculateStarRatingsForArea: (data: AppData, changedAreaName: string) => AppData;
}

// Custom hook for star rating calculation
export const useStarRatingCalculation = (): UseStarRatingCalculationReturn => {
  // Function to recalculate star ratings based on improvement areas
  const recalculateStarRatings = useCallback((data: AppData): AppData => {
    const { dimensions, relationships, improvementAreas, businessValueMetrics } = data;

    // Clone dimensions to avoid mutation
    const newDimensions: Dimension[] = [...dimensions];

    // For each dimension, calculate weighted maturity score from related areas
    newDimensions.forEach((dimension, dimIndex) => {
      // Validate relationships sum to 100%
      if (!validateRelationships(relationships, dimension.name)) {
        console.warn(`Relationships for dimension "${dimension.name}" do not sum to 100%`);
      }

      // Calculate weighted maturity score
      const weightedScore = calculateWeightedMaturityScore(relationships, improvementAreas, dimension.name);

      // Convert score to percentage (maturity levels 1-4 map to 0-100%)
      const percentage = (weightedScore / 4) * 100;

      // Calculate new stars based on percentage
      const newStars = calculateStarRating(percentage);

      // Update dimension
      newDimensions[dimIndex] = {
        ...newDimensions[dimIndex],
        stars: newStars,
        scale: getScaleFromStars(newStars),
      };
    });

    // Update business value metrics to match new star ratings
    let updatedBusinessValueMetrics: BusinessValueMetrics | undefined = businessValueMetrics;
    if (updatedBusinessValueMetrics) {
      const dimensionNameToStarsMap: DimensionStarsMap = {};
      newDimensions.forEach(dim => {
        dimensionNameToStarsMap[dim.name] = dim.stars;
      });

      updatedBusinessValueMetrics = {
        ...updatedBusinessValueMetrics,
        categories: updatedBusinessValueMetrics.categories.map(category => ({
          ...category,
          metrics: category.metrics.map(metric => ({
            ...metric,
            currentRating: dimensionNameToStarsMap[metric.name] || metric.currentRating,
          })),
        })),
      };
    }

    // Return updated data
    return {
      ...data,
      dimensions: newDimensions,
      businessValueMetrics: updatedBusinessValueMetrics,
    };
  }, []);

  // Function to recalculate star ratings for specific dimensions affected by an area change
  const recalculateStarRatingsForArea = useCallback((data: AppData, changedAreaName: string): AppData => {
    const { dimensions, relationships, improvementAreas, businessValueMetrics } = data;

    // Clone dimensions to avoid mutation
    const newDimensions: Dimension[] = [...dimensions];

    // Find all dimensions affected by this area change
    const relatedDimensions: string[] = relationships
      .filter(rel => rel.area === changedAreaName)
      .map(rel => rel.dimension);

    // Recalculate star ratings for affected dimensions only
    relatedDimensions.forEach(dimensionName => {
      const dimIndex = newDimensions.findIndex(d => d.name === dimensionName);
      if (dimIndex >= 0) {
        // Validate relationships sum to 100%
        if (!validateRelationships(relationships, dimensionName)) {
          console.warn(`Relationships for dimension "${dimensionName}" do not sum to 100%`);
        }

        // Calculate weighted maturity score using the updated improvement areas
        const weightedScore = calculateWeightedMaturityScore(relationships, improvementAreas, dimensionName);

        // Convert score to percentage (maturity levels 1-4 map to 0-100%)
        const percentage = (weightedScore / 4) * 100;

        // Calculate new stars based on percentage
        const newStars = calculateStarRating(percentage);

        // Update dimension
        newDimensions[dimIndex] = {
          ...newDimensions[dimIndex],
          stars: newStars,
          scale: getScaleFromStars(newStars),
        };
      }
    });

    // Update business value metrics to match new star ratings
    let updatedBusinessValueMetrics: BusinessValueMetrics | undefined = businessValueMetrics;
    if (updatedBusinessValueMetrics) {
      const dimensionNameToStarsMap: DimensionStarsMap = {};
      newDimensions.forEach(dim => {
        dimensionNameToStarsMap[dim.name] = dim.stars;
      });

      updatedBusinessValueMetrics = {
        ...updatedBusinessValueMetrics,
        categories: updatedBusinessValueMetrics.categories.map(category => ({
          ...category,
          metrics: category.metrics.map(metric => ({
            ...metric,
            currentRating: dimensionNameToStarsMap[metric.name] || metric.currentRating,
          })),
        })),
      };
    }

    // Return updated data
    return {
      ...data,
      dimensions: newDimensions,
      businessValueMetrics: updatedBusinessValueMetrics,
    };
  }, []);

  return {
    recalculateStarRatings,
    recalculateStarRatingsForArea,
  };
};
